import { useContext } from 'react';
import { isEmpty } from 'lodash-es';
import cls from 'classnames';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRightToBracket } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    FlexEndRow, FormContext, IconButton, SmallOutlineButton,
} from '../../../common/forms/FormElements';
import { AccordionCollapse } from '../../../common/AccordionCollapse';
import { MapDataContext } from '../MapDataContext';
import { ApiRoutes } from '../../../Common';
import { useJobQuery } from '../common/useJobQuery';
import { Job } from './Job';
import { getEmployeeId, getEmployeeMapDataForEmployee, getEmployeeName } from './common';
import { Employee } from './Employee';

export const InfoPane = () => {
    const mapData = useContext(MapDataContext);

    const { values, setFieldValue } = useContext(FormContext);
    const {
        jobId, jobGroup, selectedEmployeeId, employeeGroup,
    } = values;

    const validEmployees = [];
    const missingAddressEmployees = [];
    const missingCoordinatesEmployees = [];

    for (const e of employeeGroup ?? []) {
        if (e.homeAddress === null) {
            missingAddressEmployees.push(e);
        } else if (e.homeAddress.latitude === null || e.homeAddress.longitude === null) {
            missingCoordinatesEmployees.push(e);
        } else {
            validEmployees.push(e);
        }
    }

    const { data: jobs } = useJobQuery();
    const selectedJob = jobs?.find((job) => job.jobId === jobId);
    const selectedEmployee = employeeGroup?.find((employee) => employee.id === selectedEmployeeId);

    const { data: routeData, isLoading: routeDataLoading } = useQuery({
        queryKey: ['routeLookup', selectedJob, selectedEmployee],
        enabled: Boolean(selectedJob && selectedEmployee),
        queryFn: async () => {
            const response = await httpClient.post(ApiRoutes.route.lookup(), {
                origin: selectedEmployee.coordinates,
                destination: {
                    lat: selectedJob.meetingLatitude,
                    lng: selectedJob.meetingLongitude,
                },
                lookupTime: selectedJob.startTime,
            });
            return response;
        },
    });

    return (
        <div
            className={cls('map-right map-information-pane', (!isEmpty(jobGroup) || !isEmpty(employeeGroup)) && 'show')}
        >
            <FlexEndRow component="header">
                <IconButton
                    onClick={() => {
                        setFieldValue('jobGroup', null);
                        setFieldValue('employeeGroup', null);
                    }}
                >
                    <FontAwesomeIcon icon={faArrowRightToBracket} />
                </IconButton>
            </FlexEndRow>
            {missingAddressEmployees.length > 0 && (
                <AccordionCollapse
                    title={`${missingAddressEmployees.length} employee(s) with missing address`}
                    className="accordion-danger"
                >
                    <div className="p-2">
                        {missingAddressEmployees.map((emp) => {
                            const empId = getEmployeeId(emp);
                            return (
                                <AccordionCollapse
                                    title={getEmployeeName(emp)}
                                    key={`info-employee-missing-address-${empId}`}
                                >
                                    <Employee
                                        selected={selectedEmployeeId === empId}
                                        routeData={!routeDataLoading && routeData}
                                        routeDataLoading={routeDataLoading}
                                        mapData={getEmployeeMapDataForEmployee(empId, mapData)}
                                        {...emp}
                                    />
                                </AccordionCollapse>
                            );
                        })}
                    </div>
                </AccordionCollapse>
            )}
            {missingCoordinatesEmployees.length > 0 && (
                <AccordionCollapse
                    title={`${missingCoordinatesEmployees.length} employee(s) with missing coordinates`}
                    className="accordion-warning"
                >
                    <div className="p-2">
                        {missingCoordinatesEmployees.map((emp) => {
                            const empId = getEmployeeId(emp);
                            return (
                                <AccordionCollapse
                                    title={getEmployeeName(emp)}
                                    key={`info-employee-missing-coordinates-${empId}`}
                                >
                                    <Employee
                                        selected={selectedEmployeeId === empId}
                                        routeData={!routeDataLoading && routeData}
                                        routeDataLoading={routeDataLoading}
                                        mapData={getEmployeeMapDataForEmployee(empId, mapData)}
                                        {...emp}
                                    />
                                </AccordionCollapse>
                            );
                        })}
                    </div>
                </AccordionCollapse>
            )}
            {isEmpty(jobGroup) && selectedJob ? (
                <AccordionCollapse
                    key={`info-job-${selectedJob.jobId}`}
                    title={`${selectedJob.jobNumber} – ${selectedJob.customer || ''}`}
                    defaultOpen
                    secondaryAction={(
                        <SmallOutlineButton
                            disabled
                        >
                            Selected
                        </SmallOutlineButton>
                    )}
                >
                    <Job {...selectedJob} />
                </AccordionCollapse>
            ) : jobGroup?.map((job, index) => (
                <AccordionCollapse
                    key={`info-job-${job.jobId}`}
                    title={`${job.jobNumber} – ${job.customer || ''}`}
                    defaultOpen={jobGroup.length === 1 || index === 0}
                    secondaryAction={(
                        <SmallOutlineButton
                            onClick={() => setFieldValue('jobId', job.jobId)}
                            disabled={jobId === job.jobId}
                        >
                            {jobId === job.jobId ? 'Selected' : 'Select'}
                        </SmallOutlineButton>
                    )}
                >
                    <Job {...job} />
                </AccordionCollapse>
            ))}

            {validEmployees.map((emp, index) => {
                const empId = getEmployeeId(emp);

                return (
                    <AccordionCollapse
                        title={getEmployeeName(emp)}
                        key={`info-employee-${empId}`}
                        defaultOpen={validEmployees.length === 1 || index === 0}
                        secondaryAction={(
                            <SmallOutlineButton
                                onClick={() => setFieldValue('selectedEmployeeId', empId)}
                                disabled={selectedEmployeeId === empId}
                            >
                                {selectedEmployeeId === empId ? 'Selected' : 'Select'}
                            </SmallOutlineButton>
                        )}
                    >
                        <Employee
                            selected={selectedEmployeeId === empId}
                            routeData={!routeDataLoading && routeData}
                            routeDataLoading={routeDataLoading}
                            mapData={getEmployeeMapDataForEmployee(empId, mapData)}
                            {...emp}
                        />
                    </AccordionCollapse>
                );
            })}
        </div>
    );
};